import { GroupButtons, Select } from '@weareredlight/design-system'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type {
  AppointmentType,
  OrganizationAppointmentsParams,
} from 'types/appointments'
import type { OrganizationType } from 'types/organizations'
import type { TreatmentsType } from 'types/treatments'
import type { PaginatedRequest } from 'types/types'

import CalendarView from '../../components/Treatments/CalendarView'

import api from 'api/api'
import PageTemplate from 'components/PageTemplate'
import ScheduleNavigation from 'components/Schedule/ScheduleNavigation'
import AppointmentsList from 'components/Treatments/AppointmentsList'
import TreatmentsList from 'components/Treatments/TreatmentsList'
import { useRequest } from 'hooks/useRequest'
import { GenericPath, RootPath } from 'router/enums'
import { useCurrentUser } from 'userContext'
import { CalendarViews, ScheduleViews } from 'utils/enums'
import { enum2SelectOptions } from 'utils/forms'
import { appointmentsColumns } from 'utils/tables/appointments'
import { treatmentsColumns } from 'utils/tables/treatments'

type Option = { id: string; name: string }

const ScheduleView = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAdmin, currentUserData } = useCurrentUser()
  const [customDate, setCustomDate] = useState(new Date().toISOString())

  const [calendarFilters, setCalendarFilters] =
    useState<OrganizationAppointmentsParams>()
  const [scheduleView, setScheduleView] = useState<ScheduleViews>(
    ScheduleViews.CALENDAR,
  )
  const [calendarView, setCalendarView] = useState<CalendarViews>(
    CalendarViews.WORK_WEEK,
  )
  const [selectedOrg, setSelectedOrg] = useState(
    currentUserData?.defaultOrganizationId ?? '',
  )
  const [listView, setListView] = useState(false)
  const [openFilters, setOpenFilters] = useState(false)

  const handleCustomDateChange = (date: string) => {
    if (date && dayjs(date).isValid()) setCustomDate(date)
  }

  const handleViewChange = (value: ScheduleViews) => {
    setScheduleView(value)
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.has('page')) {
      searchParams.delete('page')
      navigate({ search: searchParams.toString() }, { replace: true })
    }
  }

  const { data: organizationsData, doRequest: getOrganizations } = useRequest<
    PaginatedRequest<OrganizationType>
  >(api.getOrganizations)

  const treatmentParams = useMemo(() => {
    const useDayRange =
      scheduleView === ScheduleViews.CALENDAR &&
      calendarView === CalendarViews.DAY

    const start = useDayRange
      ? dayjs(customDate).startOf('day')
      : dayjs(customDate).startOf('week')

    const end = useDayRange
      ? dayjs(customDate).endOf('day')
      : dayjs(customDate).endOf('week')

    return {
      startDateTime: start.format('YYYY-MM-DDT00:00:00'),
      endDateTime: end.format('YYYY-MM-DDT23:59:59'),
      organizationId: selectedOrg || '',
    }
  }, [customDate, selectedOrg, calendarView, scheduleView])

  const appointmentParams = useMemo(
    () => ({
      ...calendarFilters,
      organizationId: selectedOrg || '',
      startDateTime:
        calendarView === CalendarViews.DAY
          ? dayjs(customDate).format('YYYY-MM-DDT00:00:00')
          : dayjs(customDate).startOf('week').format('YYYY-MM-DDT00:00:00'),
      endDateTime:
        calendarView === CalendarViews.DAY
          ? dayjs(customDate).format('YYYY-MM-DDT23:59:59')
          : dayjs(customDate).endOf('week').format('YYYY-MM-DDT23:59:59'),
    }),
    [selectedOrg, calendarFilters, customDate, calendarView],
  )

  useEffect(() => {
    getOrganizations()
  }, [getOrganizations])

  return (
    <PageTemplate
      title={t('Schedule')}
      leftActions={
        // TODO:
        // - If the user is a Scheduling Admin, he will be able to select a different org (BioX Colombia should be the defautlt one)
        // - Other users will only see the org they belong to
        isAdmin && (
          <Select
            id="select"
            placeholder={t('Please select an organization')}
            value={selectedOrg}
            options={organizationsData?.data || []}
            onChange={e => setSelectedOrg(e.target.value)}
            getLabel={(option: Option) => option.name}
            getValue={(option: Option) => option.id}
            css={{
              button: {
                include: 'paragraph',
                minWidth: 'unset',
                width: 'fit-content',
                border: 'none',
                color: '$primary',
              },
              'span:last-child': {
                marginLeft: '$xxsm',
              },
            }}
          />
        )
      }
      rightActions={
        <GroupButtons
          buttons={enum2SelectOptions(ScheduleViews)}
          selectedButton={scheduleView}
          onButtonSelect={value => handleViewChange(value as ScheduleViews)}
        />
      }
    >
      <ScheduleNavigation
        scheduleView={scheduleView}
        calendarView={calendarView}
        updateCalendarView={value => setCalendarView(value as CalendarViews)}
        getShowFilters={setOpenFilters}
        showFilters={openFilters}
        getShowList={setListView}
        organizationId={selectedOrg || ''}
        showList={listView}
        customDate={customDate}
        setCustomDate={handleCustomDateChange}
        calendarFilters={calendarFilters}
      />
      {scheduleView === ScheduleViews.LIST ? (
        <TreatmentsList
          columns={treatmentsColumns as TreatmentsType[]}
          params={treatmentParams}
          onRowClick={data => {
            navigate(`${RootPath.SCHEDULE}/${data?.id}`)
          }}
        />
      ) : listView ? (
        <AppointmentsList
          calendarView={calendarView}
          columns={appointmentsColumns as AppointmentType[]}
          params={appointmentParams}
          showFilters={openFilters}
          defaultDate={customDate}
          selectedOrg={
            organizationsData?.data.find(
              organization => organization.id === selectedOrg,
            ) || ({} as OrganizationType)
          }
          setCalendarFilters={setCalendarFilters}
          onRowClick={data => {
            navigate(
              `${RootPath.SCHEDULE}/${data.treatmentProcedures?.[0].treatment.id}/${data.treatmentProcedures?.[0].id}/${GenericPath.EDIT}`,
            )
          }}
        />
      ) : (
        <CalendarView
          selectedOrg={
            organizationsData?.data.find(
              organization => organization.id === selectedOrg,
            ) || ({} as OrganizationType)
          }
          defaultDate={customDate}
          showFilters={openFilters}
          view={calendarView}
          calendarFilters={calendarFilters}
          setCalendarFilters={setCalendarFilters}
        />
      )}
    </PageTemplate>
  )
}

export default ScheduleView
